import { Button } from 'primereact/button';
import {
  CartItemInput,
  CartItemInterface,
  useAddProductsToCartMutation,
} from 'graphql/generated/magentoApi';
import { Head } from 'components/head/Head';
import { ReactElement, useEffect, useRef, useState } from 'react';
import { showErrorToast } from 'utils/errorHelpers';
import { Toast } from 'primereact/toast';
import { useCartContext } from 'CartContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import buttonize from 'utils/buttonize';
import CartItemList from 'components/CartItemList';
import Footer from 'components/footer-crwd/Footer';
import Spinner from 'components/Spinner';
import './ShoppingCartPage.scss';
import { useTranslation } from 'react-i18next';
import ProductLinked from 'components/ProductLinked';
import Slider from 'react-slick';
import { NextArrow, PrevArrow } from 'components/Galleria';
import ReactGA from 'react-ga4';
import useViewport from 'hooks/useViewport';
import ShopNavBar from './shop-home-page/shop-nav-bar';

type LinkedProduct = {
  sku: string;
  type: string;
};

export default function ShoppingCartPage(): ReactElement {
  const {
    cartId,
    items,
    loading,
    shipping,
    subTotal,
    total,
    taxes,
    updateItems,
    reset,
  } = useCartContext();
  const [updateCart, setUpdateCart] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [addProductState, addProducts] = useAddProductsToCartMutation();
  const productToAdd = searchParams.get('add-sku');
  const toast = useRef<Toast>(null);
  const { t } = useTranslation();

  const { width } = useViewport();

  const [upsellProducts, setUpsellProducts] = useState<LinkedProduct[]>([]);
  const [relatedProducts, setRelatedProducts] = useState<LinkedProduct[]>([]);

  useEffect(() => {
    if (productToAdd) {
      setTimeout(() => {
        addProducts({
          cartId,
          cartItems: [
            {
              sku: productToAdd,
              quantity: 1,
            },
          ] as CartItemInput[],
        });
      }, 500);
    }
  }, [addProducts, cartId, productToAdd]);

  useEffect(() => {
    const fetchedItems = addProductState.data?.addProductsToCart?.cart.items as
      | CartItemInterface[]
      | undefined;
    if (!addProductState.fetching && fetchedItems?.length) {
      updateItems([...fetchedItems]);
      toast.current?.show({
        severity: 'success',
        summary: 'Success',
        detail: 'Added to Cart',
        contentClassName: '',
      });
    } else if (addProductState.error) {
      showErrorToast(toast.current, addProductState.error);
      reset();
    }
  }, [addProductState, reset, updateItems]);

  useEffect(() => {
    if (items.length) {
      // upsellProducts
      const allUpsellProducts = items
        .flatMap(item => item.product.upsell_products || [])
        .map(item => ({
          sku: item?.sku,
          type: 'upsell',
        }))
        .filter(
          filteredItem =>
            !items.some(item => item.product.sku === filteredItem.sku),
        );
      const cleanedUpsell = new Set(
        allUpsellProducts.map(item => JSON.stringify(item)),
      );
      const noDuplicatesUpsellProducts = Array.from(cleanedUpsell).map(item =>
        JSON.parse(item),
      );

      const oddElements = noDuplicatesUpsellProducts.filter(
        (_, i) => i % 2 !== 0,
      );
      const evenElements = noDuplicatesUpsellProducts.filter(
        (_, i) => i % 2 === 0,
      );
      setUpsellProducts([...evenElements, ...oddElements] as LinkedProduct[]);

      // relatedProducts
      const allRelatedProducts = items
        .flatMap(item => item.product.related_products || [])
        .map(item => ({
          sku: item?.sku,
          type: 'related',
        }))
        .filter(
          filteredItem =>
            !items.some(item => item.product.sku === filteredItem.sku),
        );
      const cleanedRelated = new Set(
        allRelatedProducts.map(item => JSON.stringify(item)),
      );
      const noDuplicatesRelatedProducts = Array.from(cleanedRelated).map(item =>
        JSON.parse(item),
      );
      setRelatedProducts(noDuplicatesRelatedProducts as LinkedProduct[]);
    }
  }, [items, items.length]);

  const getSlidesToShow = (): number => {
    if (width >= 0 && width < 576) {
      return 2;
    }
    if (width >= 768 && width < 992) {
      return 4;
    }
    return 3;
  };

  const settings = {
    accessibility: true,
    prevArrow: <PrevArrow />,
    nextArrow: <NextArrow />,
    dots: false,
    infinite: true,
    speed: 500,
    slidesToShow: getSlidesToShow(),
    slidesToScroll: 1,
    arrows: true,
    draggable: true,
  };

  if (loading) return <Spinner />;

  return (
    <>
      <ShopNavBar />
      <Head
        title="Shopping Cart"
        link="/cart"
        keywords="cart, checkout, blockchain shopping cart, blockchain market cart, blockchain checkout"
      />
      <div
        className="grid cart align-items-center p-4"
        style={{ maxWidth: '1140px', margin: '0 auto' }}
      >
        <p
          {...buttonize(() => navigate(-1))}
          className="text-primary no-underline cursor-pointer uppercase mt-0 mb-2 text-xs"
          style={{
            fontFamily: 'Raleway, sans-serif',
            fontWeight: 600,
            letterSpacing: '2px',
          }}
        >
          {t('go_back')}
        </p>
        {items.length ? (
          <>
            <CartItemList
              updateCart={updateCart}
              cleanState={() => setUpdateCart(false)}
            />
            <div className="flex flex-column col-12 align-items-end mb-2">
              <p className="text-700 font-light m-0">
                {t('subtotal')}: <span className="text-900">${subTotal}</span>
              </p>
              <p className="text-700 font-light m-0">
                {t('shipping')}: <span className="text-900">{shipping}</span>
              </p>
              <p className="text-700 font-light m-0">
                {t('taxes')}:{' '}
                <span className="text-900">
                  {taxes !== '0.00' ? `$${taxes}` : 'Calculated at checkout'}
                </span>
              </p>
              <p className="text-700 font-light mt-2 mb-3 text-2xl">
                {t('total')}: <span className="text-900">${total}</span>
              </p>
              <Button
                className="uppercase border-noround c-button-primary"
                label="Checkout"
                onClick={() => {
                  ReactGA.event({
                    category: 'ecommerce',
                    action: 'cart_checkout_button',
                    label: 'Cart Checkout Button',
                  });
                  setUpdateCart(true);
                }}
              />
            </div>
            {!!(upsellProducts.length > 0 || relatedProducts.length > 0) && (
              <div className="grid m-0 px-2 lg:px-0 justify-content-between w-full">
                {upsellProducts.length > 0 && (
                  <div className="col-12 lg:col-5">
                    <h3 className="mb-2 uppercase">{`Save more with ${
                      upsellProducts.length === 1 ? 'a bundle' : 'bundles'
                    }!`}</h3>
                    {upsellProducts.length < 3 ? (
                      <div className="grid">
                        {upsellProducts.map(productLinked => (
                          <ProductLinked
                            key={productLinked.sku}
                            sku={productLinked.sku}
                            type={productLinked.type}
                            isGrid
                          />
                        ))}
                      </div>
                    ) : (
                      <Slider
                        {...settings}
                        className="cart-page__slider w-full mb-4"
                      >
                        {upsellProducts.map(productLinked => (
                          <ProductLinked
                            key={productLinked.sku}
                            sku={productLinked.sku}
                            type={productLinked.type}
                          />
                        ))}
                      </Slider>
                    )}
                  </div>
                )}
                {relatedProducts.length > 0 && (
                  <div className="col-12 lg:col-5">
                    <h3 className="mb-2 uppercase">{`Related Product${
                      relatedProducts.length === 1 ? '' : 's'
                    }`}</h3>
                    {relatedProducts.length < 3 ? (
                      <div className="grid">
                        {relatedProducts.map(productLinked => (
                          <ProductLinked
                            key={productLinked.sku}
                            sku={productLinked.sku}
                            type={productLinked.type}
                            isGrid
                          />
                        ))}
                      </div>
                    ) : (
                      <Slider
                        {...settings}
                        className="cart-page__slider w-full mb-4"
                      >
                        {relatedProducts.map(productLinked => (
                          <ProductLinked
                            key={productLinked.sku}
                            sku={productLinked.sku}
                            type={productLinked.type}
                          />
                        ))}
                      </Slider>
                    )}
                  </div>
                )}
              </div>
            )}
          </>
        ) : (
          <>
            <div
              className="w-full text-center font-medium"
              style={{ minHeight: '40vh' }}
            >
              <h1>Empty cart</h1>
              <p>Please, add your first item.</p>
            </div>
          </>
        )}
      </div>

      <Toast
        ref={toast}
        position="top-right"
        className="cart-page__toast fixed"
        onHide={() => {
          searchParams.delete('add-sku');
        }}
      />
      <Footer />
    </>
  );
}
